import { DateTime } from 'luxon'

import {
  formatWithZoneLabel,
  instantInZone,
} from '../../ts-utils/luxon-permanent-dst'
import { DAYS_OF_WEEK } from './constants'
import { getEventRecurrence } from './getEventRecurrence'
import type { SnEvent } from './types'

type RecurrenceFields = Pick<
  SnEvent,
  'recurrence_type' | 'series' | 'days_of_week' | 'repeat_until'
>

export interface FormattedRecurrence {
  /** 'Weekly' | 'Biweekly' | 'Monthly'. */
  type: string
  /** e.g. 'Sep 1, 2026 · 6:00 PM PDT' (abbreviation only when a viewerZone
   *  was given), or '' when the API omits the cut-off. */
  repeatUntil: string
  /** e.g. 'Tuesday, Thursday', or '' for monthly / when absent. */
  days: string
}

/**
 * A UTC instant in the viewer's own zone, matching how `starts_at` is rendered
 * everywhere else. Unlike the old wall-clock rule fields, `repeat_until` is a
 * real instant, so converting it is correct rather than misleading.
 *
 * With a `viewerZone` the result carries the zone abbreviation ('· 12:30 PM
 * PDT'), matching the hero above it — a bare time on a screen whose other
 * times are zone-labelled reads as though it belongs to no clock in
 * particular. Without one, Luxon's default zone is used and no abbreviation
 * is appended, preserving the original single-argument behaviour.
 */
const formatInstant = (
  iso: string | null | undefined,
  viewerZone?: string | null
): string => {
  if (!iso) {
    return ''
  }

  //  Via `instantInZone` so a zone that abolished its clock change reads on
  //  the offset it kept. See ts-utils/luxon-permanent-dst.
  const parsed = viewerZone
    ? instantInZone(iso, viewerZone)
    : DateTime.fromISO(iso)

  // setZone with an unknown identifier yields an invalid DateTime; fall back
  // to the default zone rather than rendering nothing for a real instant.
  if (!parsed.isValid) {
    const fallback = DateTime.fromISO(iso)
    return fallback.isValid ? fallback.toFormat('MMM d, yyyy · h:mm a') : ''
  }

  //  The label goes back to the one residents use; the substituted zone would
  //  otherwise be named after the standard zone sharing its offset.
  return viewerZone
    ? formatWithZoneLabel(parsed, viewerZone, 'MMM d, yyyy · h:mm a ZZZZ')
    : parsed.toFormat('MMM d, yyyy · h:mm a')
}

/**
 * Human-readable repeat rule for the detail page, or null for a one-off.
 *
 * The rule's START is the event's own `starts_at`, so only the cut-off has a
 * field of its own. Each part is returned as '' when absent so the UI can hide
 * just that line rather than the whole section.
 */
export const formatRecurrence = (
  event: RecurrenceFields | null | undefined,
  /** Viewer's zone. Given, the cut-off carries its abbreviation. */
  viewerZone?: string | null
): FormattedRecurrence | null => {
  // Reads the nested `series` rule first, falling back to the flat fields.
  const recurrence = getEventRecurrence(event)
  if (!recurrence) {
    return null
  }

  const days = recurrence.daysOfWeek
    .map(
      (value) => DAYS_OF_WEEK.find((day) => day.value === value)?.label ?? ''
    )
    .filter(Boolean)
    .join(', ')

  const { recurrenceType } = recurrence
  return {
    type: recurrenceType.charAt(0).toUpperCase() + recurrenceType.slice(1),
    repeatUntil: formatInstant(recurrence.repeatUntil, viewerZone),
    days,
  }
}
